import { MachineNotFoundError } from '@internal/utils/errors/machineErrors'
import { TLocale } from '@internal/utils/machine/i18n/Locale'
import { Machine } from '@internal/utils/machine/Machine'
import { SearchPayload } from '@internal/utils/search/SearchPayload'
import { SortOrder } from '@internal/utils/search/SortOrder'
import axios, { AxiosError, AxiosRequestHeaders } from 'axios'
import { environment } from 'environment/environment'
import { SetOptional } from 'type-fest'
import { getAxiosData } from 'utils/api/getAxiosData'
import { TSearchResult } from './TSearchResult'
import { GetSuggestResponse } from './types/suggestTypes'
import { WebsiteForm } from '@internal/utils/form'

const apiClient = axios.create({
  baseURL: `${environment.OWN_URL}/api`,
  timeout: 30000,
})

export type MachineMeta = Pick<
  Machine,
  'machineGroupId' | 'brand' | 'name' | 'machineType' | 'model' | 'pdp'
> & {
  mappedModel: string | null
  baseModel: string | null
  rawModel: string | null
}

export const getMachineMeta = async ({
  machineGroupId,
  ip,
  resolveMerged,
}: {
  machineGroupId: string
  resolveMerged?: boolean
  ip?: string
}) => {
  const headers: AxiosRequestHeaders = {}
  if (ip) {
    headers['x-forwarded-for'] = ip
  }
  return apiClient
    .get<MachineMeta>(
      `/getMachineMeta/?machineGroupId=${machineGroupId}${
        resolveMerged ? '&resolveMerged=true' : ''
      }`,
      {
        headers,
      }
    )
    .then(getAxiosData)
    .catch((err) => {
      const axiosError = err as AxiosError
      if (axiosError.isAxiosError && axiosError.response?.status === 404) {
        throw new MachineNotFoundError(machineGroupId)
      }
      throw err
    })
}

export const getSerpUrlForAlternatives = async (params: {
  noCache?: boolean
  refreshCache?: boolean
  locale: TLocale
  machine: MachineMeta
  ip?: string
}) =>
  apiClient
    .post<{ url: string }>(`/getSerpUrlForAlternatives/`, params)
    .then(getAxiosData)

export const getMachinesCount = (params: {
  search: SetOptional<SearchPayload, 'limit' | 'offset' | 'sort'>
  abortController?: AbortController
  userCountry: string | null
  ip: string | null
  noCache: boolean
  refreshCache: boolean
}) =>
  searchMachines({
    ...params,
    search: {
      ...params.search,
      limit: 1,
      offset: 0,
      sort: { by: 'relevance', order: SortOrder.Ascending },
    },
  }).then((res) => res.count)

export const searchMachines = async ({
  search,
  abortController,
  userCountry,
  ip,
  noCache,
  refreshCache,
}: {
  search: SearchPayload
  abortController?: AbortController
  userCountry: string | null
  ip: string | null
  noCache: boolean
  refreshCache: boolean
}) =>
  apiClient
    .post<TSearchResult>(
      `/searchMachines/`,
      {
        search,
        userCountry,
        ip,
        disableCache: !!noCache,
        forceUpdateCache: !!refreshCache,
      },
      {
        signal: abortController?.signal,
      }
    )
    .then(getAxiosData)

export const suggest = (
  query: string,
  locale: TLocale,
  abortSignal?: AbortSignal
): Promise<GetSuggestResponse> =>
  apiClient
    .get<GetSuggestResponse>(`/suggest/`, {
      signal: abortSignal,
      params: {
        query,
        locale,
      },
    })
    .then(getAxiosData)

export const postForm = (data: WebsiteForm): Promise<{ success: boolean }> =>
  apiClient.post(`/forms/`, data).then((res) => res.data)
